'use client'

import { useState } from 'react'
import dynamic from 'next/dynamic'
import { useForm } from 'react-hook-form'
import { Mail, Phone, Send, CheckCircle2, XCircle, Linkedin, Instagram, Github, Globe } from 'lucide-react'

const MorphingBlob = dynamic(() => import('@/components/three/MorphingBlob'), {
  ssr: false,
  loading: () => null,
})

interface FormData {
  name: string
  email: string
  phone?: string
  service: string
  brief: string
  budget?: string
}

const services = [
  'IT — Web Development',
  'IT — Full Stack Development',
  'IT — WordPress',
  'IT — UI/UX Design',
  'IT — SEO',
  'Videography',
  'Graphic Design',
  'Other',
]

const budgets = [
  'Under ₹50,000',
  '₹50,000 – ₹1,00,000',
  '₹1,00,000 – ₹5,00,000',
  '₹5,00,000+',
  'Let\'s discuss',
]

const socials = [
  { icon: Linkedin, href: '#', label: 'LinkedIn' },
  { icon: Instagram, href: '#', label: 'Instagram' },
  { icon: Globe, href: '#', label: 'Behance' },
  { icon: Github, href: '#', label: 'GitHub' },
]

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>()

  const onSubmit = async (data: FormData) => {
    setStatus('loading')
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (res.ok) {
        setStatus('success')
        reset()
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  const inputClass = 'input-gold w-full px-4 py-3.5 text-sm rounded-none outline-none'
  const labelClass = 'text-[#94A3B8] text-xs tracking-[0.15em] mb-1.5 block'

  return (
    <div className="bg-[#0F172A] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 pt-40 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* LEFT side */}
          <div className="flex flex-col">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-[#C8A96E]" />
              <span className="text-[#C8A96E] text-xs tracking-[0.3em]" style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}>
                CONTACT
              </span>
            </div>

            <h1
              className="text-[clamp(4rem,10vw,9rem)] leading-none text-[#F8FAFC] mb-8"
              style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.02em' }}
            >
              LET'S<br />TALK
            </h1>

            {/* Contact details */}
            <div className="flex flex-col gap-4 mb-10">
              <a
                href="mailto:info@apexvisualtech.cloud"
                className="flex items-center gap-3 text-[#C8A96E] hover:text-[#D4BA8A] transition-colors"
                style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
              >
                <Mail size={16} />
                <span className="text-sm">info@apexvisualtech.cloud</span>
              </a>
              <a
                href="tel:+919876543210"
                className="flex items-center gap-3 text-[#94A3B8] hover:text-[#C8A96E] transition-colors"
                style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
              >
                <Phone size={16} />
                <span className="text-sm">+91 98765 43210</span>
              </a>
              <div
                className="flex items-start gap-3 text-[#94A3B8] mt-2"
                style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
              >
                <Globe size={16} className="mt-1" />
                <address className="not-italic text-sm leading-loose">
                  Level 4, DLF Cyber City<br />
                  New Delhi, Delhi NCR<br />
                  India — 110001
                </address>
              </div>
            </div>

            {/* Socials */}
            {/* <div className="flex gap-4 mb-12">
              {socials.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="w-10 h-10 border border-white/10 flex items-center justify-center text-[#94A3B8] hover:border-[#C8A96E] hover:text-[#C8A96E] transition-all duration-300"
                >
                  <Icon size={15} />
                </a>
              ))}
            </div> */}

            {/* 3D blob */}
            <div className="flex-1 min-h-[280px] hidden lg:block">
              <MorphingBlob />
            </div>
          </div>

          {/* RIGHT side — Form */}
          <div>
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[500px] gap-6 border border-[#C8A96E]/20 p-12">
                <CheckCircle2 size={48} className="text-[#C8A96E]" />
                <h2 className="text-4xl text-[#F8FAFC] text-center" style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.05em' }}>
                  MESSAGE SENT
                </h2>
                <p className="text-[#94A3B8] text-center text-sm max-w-xs">
                  We've received your message and will get back to you within 24 hours.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-4 text-[#C8A96E] text-xs tracking-[0.15em] border border-[#C8A96E]/30 px-6 py-2.5 hover:bg-[#C8A96E] hover:text-[#0F172A] transition-all"
                  style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
                >
                  SEND ANOTHER
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                {/* Name */}
                <div>
                  <label className={labelClass} style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}>FULL NAME *</label>
                  <input
                    {...register('name', { required: 'Name is required' })}
                    className={inputClass}
                    style={{ fontFamily: 'var(--font-dm-sans, "DM Sans", sans-serif)', background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(148,163,184,0.3)', color: '#F8FAFC' }}
                    placeholder="Your full name"
                  />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name.message}</p>}
                </div>

                {/* Email + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass} style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}>EMAIL *</label>
                    <input
                      {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' } })}
                      className={inputClass}
                      style={{ fontFamily: 'var(--font-dm-sans, "DM Sans", sans-serif)', background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(148,163,184,0.3)', color: '#F8FAFC' }}
                      placeholder="your@email.com"
                    />
                    {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>}
                  </div>
                  <div>
                    <label className={labelClass} style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}>PHONE (OPTIONAL)</label>
                    <input
                      {...register('phone')}
                      className={inputClass}
                      style={{ fontFamily: 'var(--font-dm-sans, "DM Sans", sans-serif)', background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(148,163,184,0.3)', color: '#F8FAFC' }}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>

                {/* Service */}
                <div>
                  <label className={labelClass} style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}>SERVICE INTERESTED IN *</label>
                  <select
                    {...register('service', { required: 'Please select a service' })}
                    className={inputClass}
                    style={{ fontFamily: 'var(--font-dm-sans, "DM Sans", sans-serif)', background: 'rgba(30,41,59,0.95)', border: '1px solid rgba(148,163,184,0.3)', color: '#F8FAFC' }}
                  >
                    <option value="" disabled>Select a service</option>
                    {services.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.service && <p className="text-red-400 text-xs mt-1">{errors.service.message}</p>}
                </div>

                {/* Brief */}
                <div>
                  <label className={labelClass} style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}>PROJECT BRIEF *</label>
                  <textarea
                    {...register('brief', { required: 'Please describe your project' })}
                    rows={5}
                    className={inputClass}
                    style={{ fontFamily: 'var(--font-dm-sans, "DM Sans", sans-serif)', background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(148,163,184,0.3)', color: '#F8FAFC', resize: 'none' }}
                    placeholder="Tell us about your project, goals, and timeline..."
                  />
                  {errors.brief && <p className="text-red-400 text-xs mt-1">{errors.brief.message}</p>}
                </div>

                {/* Budget */}
                <div>
                  <label className={labelClass} style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}>BUDGET RANGE (OPTIONAL)</label>
                  <select
                    {...register('budget')}
                    className={inputClass}
                    style={{ fontFamily: 'var(--font-dm-sans, "DM Sans", sans-serif)', background: 'rgba(30,41,59,0.95)', border: '1px solid rgba(148,163,184,0.3)', color: '#F8FAFC' }}
                  >
                    <option value="">Prefer not to say</option>
                    {budgets.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>

                {/* Error state */}
                {status === 'error' && (
                  <div className="flex items-center gap-2 text-red-400 text-sm border border-red-400/20 px-4 py-3">
                    <XCircle size={16} />
                    Failed to send. Please try again or email us directly.
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="mt-2 flex items-center justify-center gap-3 bg-[#C8A96E] text-[#0F172A] px-8 py-4 font-semibold tracking-[0.15em] hover:bg-[#D4BA8A] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)', fontSize: '0.8rem' }}
                >
                  {status === 'loading' ? (
                    <>SENDING...</>
                  ) : (
                    <>SEND MESSAGE <Send size={14} /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
